import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency';
import {
  TrendingUp, TrendingDown, Wallet, Plus, Trash2, LogOut,
  LayoutDashboard, Receipt, Settings, ShieldCheck, Loader2, Menu, X,
  AlertTriangle, CheckCircle, Lightbulb, PieChart, Download, Target,
  CalendarIcon, ArrowUpRight, ArrowDownRight, MoreHorizontal, ArrowLeftRight,
  CalendarClock, Sparkles, PiggyBank, Tag, Repeat, BarChart3, Upload,
  Image as ImageIcon
} from 'lucide-react';import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend
} from 'recharts';
import {
  getTransactions, createTransaction, deleteTransaction, getTransactionStats
} from '@/services/api';
import ReceiptUploader from '@/components/ReceiptUploader';
import OnboardingModal from '@/components/OnboardingModal';
import '@/App.css';

const COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#6366F1', '#14B8A6'];

const EXPENSE_CATEGORIES = ['Alimentación', 'Transporte', 'Vivienda', 'Servicios', 'Entretenimiento', 'Salud', 'Educación', 'Otros'];
const INCOME_CATEGORIES = ['Salario', 'Freelance', 'Inversiones', 'Ventas', 'Otros'];
const TRANSFER_CATEGORY = 'Transferencia';
const normalizeTransactionType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const aliases = {
    income: 'income',
    ingreso: 'income',
    receita: 'income',
    renda: 'income',
    expense: 'expense',
    gasto: 'expense',
    despesa: 'expense',
    transfer: 'transfer',
    transferencia: 'transfer',
    transferência: 'transfer',
  };
  return aliases[normalized] || normalized || 'expense';
};
const accountTypeSuffix = (type) => {
  if (type === 'credit_card') return ' · tarjeta';
  if (type === 'investment') return ' · inversiones';
  return '';
};

const DashboardPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [formData, setFormData] = useState({
    type: 'expense', amount: '', category: '', description: '',
    date: new Date(), account_id: '', to_account_id: '', installments_total: 1, notes: '',
    receipt_base64: ''
  });
  const [formError, setFormError] = useState('');
  const [budgetForm, setBudgetForm] = useState({ category: '', amount: '' });
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrMsg, setOcrMsg] = useState('');
  const ocrInputRef = useRef(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [transRes, statsRes, summaryRes, insightsRes, accountsRes, categoriesRes] = await Promise.allSettled([
        getTransactions(),
        getTransactionStats(),
        fetch(`${API_URL}/api/dashboard/summary`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/insights`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/accounts`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/categories`, { headers }).then(r => r.ok ? r.json() : [])
      ]);

      if (transRes.status === 'fulfilled')   setTransactions(transRes.value);
      if (statsRes.status === 'fulfilled')   setStats(statsRes.value);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      if (accountsRes.status === 'fulfilled') setAccounts(accountsRes.value || []);
      if (categoriesRes.status === 'fulfilled') setUserCategories(categoriesRes.value || []);
      if (insightsRes.status === 'fulfilled' && insightsRes.value) {
        setInsights(insightsRes.value.insights || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Show onboarding wizard once for new users with no data
  useEffect(() => {
    let cancelled = false;
    const checkOnboarding = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.needs_onboarding) setShowOnboarding(true);
      } catch { /* ignore */ }
    };
    checkOnboarding();
    return () => { cancelled = true; };
  }, [API_URL]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      setFormError('');
      if (formData.type === 'transfer') {
        if (!formData.account_id || !formData.to_account_id) {
          setFormError(t('selectAccount'));
          return;
        }
        if (formData.account_id === formData.to_account_id) {
          setFormError(t('sameAccountError'));
          return;
        }
      }
      const installments = Math.max(1, parseInt(formData.installments_total, 10) || 1);
      const tagsArray = (formData.tags || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
      await createTransaction({
        type: formData.type,
        amount: parseFloat(formData.amount),
        category: formData.type === 'transfer' ? TRANSFER_CATEGORY : formData.category,
        description: formData.description,
        date: formData.date ? format(formData.date, 'yyyy-MM-dd') : undefined,
        account_id: formData.account_id || null,
        to_account_id: formData.type === 'transfer' ? formData.to_account_id : null,
        installments_total: formData.type === 'transfer' ? 1 : installments,
        notes: formData.notes || null,
        tags: tagsArray.length > 0 ? tagsArray : null,
        receipt_base64: formData.receipt_base64 || null
      });
      setDialogOpen(false);
      setFormData({
        type: 'expense', amount: '', category: '', description: '',
        date: new Date(), account_id: '', to_account_id: '', installments_total: 1, notes: '',
        tags: '', receipt_base64: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating transaction');
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (window.confirm('¿Eliminar esta transacción?')) {
      try { await deleteTransaction(id); fetchData(); }
      catch { console.error('Error deleting transaction'); }
    }
  };

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7);
      await fetch(`${API_URL}/api/budgets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: budgetForm.category,
          amount: parseFloat(budgetForm.amount),
          month: currentMonth
        })
      });
      setBudgetDialogOpen(false);
      setBudgetForm({ category: '', amount: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating budget');
    }
  };

  const handleExport = async (fmtType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/export/transactions?format=${fmtType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = fmtType === 'csv'
        ? await response.blob()
        : new Blob([JSON.stringify(await response.json(), null, 2)],
                    { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fmtType === 'csv' ? 'transacciones.csv' : 'transacciones.json';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting');
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'danger':  return <AlertTriangle className="h-5 w-5 text-red-400" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
      case 'success': return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'tip':     return <Lightbulb className="h-5 w-5 text-blue-400" />;
      default:        return <PieChart className="h-5 w-5 text-violet-400" />;
    }
  };

  const getInsightBg = (type) => {
    switch (type) {
      case 'danger':  return 'bg-red-500/10 border-red-500/30';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'success': return 'bg-green-500/10 border-green-500/30';
      case 'tip':     return 'bg-blue-500/10 border-blue-500/30';
      default:        return 'bg-violet-500/10 border-violet-500/30';
    }
  };

  const handleOcrPick = () => {
    setOcrMsg('');
    ocrInputRef.current?.click();
  };

  const handleOcrFile = async (e) => {
    const file = e.target.files?.[0];
    if (ocrInputRef.current) ocrInputRef.current.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setOcrMsg('Solo imágenes (JPG/PNG) son válidas para OCR');
      return;
    }
    setOcrBusy(true);
    setOcrMsg('');
    try {
      // Compress the image client-side before sending to keep payload small
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('read error'));
        reader.onload = () => {
          const img = new Image();
          img.onerror = () => reject(new Error('image error'));
          img.onload = () => {
            const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            c.toBlob(
              (b) => {
                if (!b) return resolve(String(reader.result));
                const fr2 = new FileReader();
                fr2.onload = () => resolve(String(fr2.result));
                fr2.readAsDataURL(b);
              },
              'image/jpeg', 0.8
            );
          };
          img.src = String(reader.result);
        };
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/receipts/ocr`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: dataUrl }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOcrMsg(data.detail || 'No se pudo procesar la foto');
        return;
      }

      // Pre-fill form with whatever the model returned
      const next = { ...formData };
      if (data.amount) next.amount = String(data.amount);
      if (data.date) {
        const d = new Date(data.date + 'T00:00:00');
        if (!isNaN(d.getTime())) next.date = d;
      }
      if (data.description) next.description = data.description;
      if (data.suggested_type) next.type = data.suggested_type;
      if (data.suggested_category) next.category = data.suggested_category;
      // Also attach the photo as the receipt
      next.receipt_base64 = dataUrl;
      setFormData(next);

      const conf = data.confidence === 'high' ? '✓ alta confianza'
                 : data.confidence === 'medium' ? '⚠ confianza media — revisa los datos'
                 : '⚠ confianza baja — revisa los datos';
      setOcrMsg(`✨ Datos extraídos · ${conf}`);
      setTimeout(() => setOcrMsg(''), 6000);
    } catch (err) {
      setOcrMsg('Error: ' + (err.message || ''));
    } finally {
      setOcrBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </div>
    );
  }

  const categories = formData.type === 'transfer'
    ? [TRANSFER_CATEGORY]
    : (userCategories.length > 0
    ? userCategories.filter(c => c.type === formData.type && c.is_active).map(c => c.name)
    : (formData.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES));

  // ---- DERIVED DATA ----
  const realBalance      = summary?.real_balance ?? stats?.balance ?? 0;
  const monthIncome      = summary?.current_month?.income  ?? 0;
  const monthExpense     = summary?.current_month?.expense ?? 0;
  const availableSpend   = summary?.available_to_spend ?? (monthIncome - monthExpense);
  const availableMode    = summary?.available_mode ?? 'cashflow';
  const incomeChange     = summary?.changes?.income_percentage;
  const expenseChange    = summary?.changes?.expense_percentage;
  const topCategories    = summary?.top_categories || [];
  const upcomingBills    = summary?.upcoming_bills || [];
  const budgetSummary    = summary?.budget_summary;
  const budgets          = summary?.budgets || [];
  const accountsSummary  = summary?.accounts_summary;
  const goalsSummary     = summary?.goals_summary;
  const debtsSummary     = summary?.debts_summary;
  const hasNoData        = transactions.length === 0;

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {showOnboarding && (
        <OnboardingModal
          initialCurrency={currency}
          initialLocale={locale}
          onDone={() => { setShowOnboarding(false); fetchData(); }}
        />
      )}
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú de navegación">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-400 hover:text-white p-2 -mr-2"
                aria-label="Cerrar menú"
                data-testid="mobile-menu-close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-2" aria-label="Navegación principal">
              <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20" onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="h-5 w-5" /> Dashboard
              </Link>
              <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Receipt className="h-5 w-5" /> {t('transactions')}
              </Link>
              <Link to="/accounts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Wallet className="h-5 w-5" /> {t('accounts')}
              </Link>
              <Link to="/categories" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Tag className="h-5 w-5" /> {t('category')}
              </Link>
              <Link to="/recurring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Repeat className="h-5 w-5" /> Recurrentes
              </Link>
              <Link to="/budgets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Target className="h-5 w-5" /> {t('budgets')}
              </Link>
              <Link to="/goals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <PiggyBank className="h-5 w-5" /> {t('goalsAndDebts')}
              </Link>
              <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="h-5 w-5" /> {t('reports')}
              </Link>
              <Link to="/receipts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <ImageIcon className="h-5 w-5" /> {t('receipts')}
              </Link>
              <Link to="/import" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Upload className="h-5 w-5" /> {t('importItau')}
              </Link>
              <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" /> {t('settings')}
              </Link>
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <ShieldCheck className="h-5 w-5" /> Admin Panel
                </Link>
              )}
            </nav>
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <p className="text-sm text-zinc-400">Conectado como</p>
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">{t('financePanel')}</p>
        </div>
        <nav className="space-y-2" aria-label="Navegación principal">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20">
            <LayoutDashboard className="h-5 w-5" /> {t('dashboard')}
          </Link>
          <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Receipt className="h-5 w-5" /> {t('transactions')}
          </Link>
          <Link to="/accounts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Wallet className="h-5 w-5" /> {t('accounts')}
          </Link>
          <Link to="/categories" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Tag className="h-5 w-5" /> {t('category')}
          </Link>
          <Link to="/recurring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Repeat className="h-5 w-5" /> Recurrentes
          </Link>
          <Link to="/budgets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Target className="h-5 w-5" /> {t('budgets')}
          </Link>
          <Link to="/goals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <PiggyBank className="h-5 w-5" /> {t('goalsAndDebts')}
          </Link>
          <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <BarChart3 className="h-5 w-5" /> {t('reports')}
          </Link>
          <Link to="/receipts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <ImageIcon className="h-5 w-5" /> {t('receipts')}
          </Link>
          <Link to="/import" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Upload className="h-5 w-5" /> {t('importItau')}
          </Link>
          <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
            <Settings className="h-5 w-5" /> {t('settings')}
          </Link>
          {isAdmin && (
            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:bg-zinc-800/50 transition-colors">
              <ShieldCheck className="h-5 w-5" /> {t('adminPanelShort')}
            </Link>
          )}
        </nav>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">{t('connectedAs')}</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        {/* Header: stacks on mobile, no horizontal overflow */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden text-zinc-300 hover:text-white p-2 -ml-2 rounded-lg hover:bg-zinc-800"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
              data-testid="mobile-menu-open"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white truncate">{t('dashboard')}</h2>
              <p className="text-zinc-400 text-sm truncate">{t('welcome')} {user?.name}</p>
            </div>
          </div>

          {/* Actions row — wraps on mobile, never overflows */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop: full buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200" data-testid="export-button">
                    <Download className="h-4 w-4 mr-2" /> Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                  <DropdownMenuLabel className="text-zinc-400">Formato</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('json')} data-testid="export-json">JSON</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                onClick={() => setBudgetDialogOpen(true)}
                data-testid="budget-button"
              >
                <Target className="h-4 w-4 mr-2" /> {t('budgets')}
              </Button>
            </div>

            {/* Primary CTA — always visible */}
            <Button
              className="bg-violet-600 hover:bg-violet-700 shrink-0"
              onClick={() => setDialogOpen(true)}
              data-testid="new-transaction-button"
              aria-label="Crear nueva transacción"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('newTransaction')}</span>
              <span className="sm:hidden ml-1">{t('newTransaction')}</span>
            </Button>

            {/* Mobile: collapse Export & Budget into a "more" menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-zinc-700 hover:bg-zinc-800 text-zinc-200 shrink-0"
                    aria-label="Más acciones"
                    data-testid="mobile-more-actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-48">
                  <DropdownMenuItem onClick={() => setBudgetDialogOpen(true)} data-testid="mobile-action-budget">
                    <Target className="h-4 w-4 mr-2" /> Presupuesto
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuLabel className="text-zinc-400 text-xs">Exportar como</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="mobile-export-csv">
                    <Download className="h-4 w-4 mr-2" /> CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('json')} data-testid="mobile-export-json">
                    <Download className="h-4 w-4 mr-2" /> JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* DIALOGS */}
            <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Definir Presupuesto</DialogTitle>
                  <DialogDescription>Define un límite mensual por categoría</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateBudget} className="space-y-4">
                  <div>
                    <Label htmlFor="budget-cat">Categoría</Label>
                    <Select value={budgetForm.category} onValueChange={(v) => setBudgetForm({ ...budgetForm, category: v })}>
                      <SelectTrigger id="budget-cat" className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPENSE_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="budget-amount">Monto</Label>
                    <Input
                      id="budget-amount"
                      inputMode="decimal"
                      type="number"
                      step="0.01"
                      value={budgetForm.amount}
                      onChange={(e) => setBudgetForm({ ...budgetForm, amount: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="500.00"
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-violet-600 hover:bg-violet-700">Guardar</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('newTransaction')}</DialogTitle>
                  <DialogDescription>{t('transactionTypeHelp')}</DialogDescription>
                </DialogHeader>
                <input
                  ref={ocrInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleOcrFile}
                  className="hidden"
                  data-testid="ocr-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 text-violet-200"
                  onClick={handleOcrPick}
                  disabled={ocrBusy}
                  data-testid="ocr-btn"
                >
                  {ocrBusy
                    ? <span className="inline-flex items-center"><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Leyendo recibo…</span>
                    : <><Sparkles className="h-3.5 w-3.5 mr-2" /> Pre-llenar desde foto del recibo</>}
                </Button>
                {ocrMsg && (
                  <p className="text-xs text-violet-200 -mt-2" data-testid="ocr-msg">{ocrMsg}</p>
                )}
                {formError && (
                  <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="tx-form-error">
                    {formError}
                  </p>
                )}
                <form onSubmit={handleCreateTransaction} className="space-y-4">
                  <div>
                    <Label htmlFor="tx-type">{t('type')}</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, category: v === 'transfer' ? TRANSFER_CATEGORY : '', to_account_id: '' })}>
                      <SelectTrigger id="tx-type" className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">{t('income')}</SelectItem>
                        <SelectItem value="expense">{t('expense')}</SelectItem>
                        <SelectItem value="transfer">
                          <span className="inline-flex items-center gap-2"><ArrowLeftRight className="h-3.5 w-3.5" /> {t('transfer')}</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.type !== 'transfer' && (
                  <div>
                    <Label htmlFor="tx-cat">{t('category')}</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger id="tx-cat" className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  <div>
                    <Label htmlFor="tx-amount">{t('amount')}</Label>
                    <Input
                      id="tx-amount"
                      inputMode="decimal"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="0.00"
                      required
                      data-testid="tx-amount-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tx-desc">{t('description')}</Label>
                    <Input
                      id="tx-desc"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Descripción opcional"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tx-account">{formData.type === 'transfer' ? t('fromAccount') : t('accounts')}</Label>
                    <Select
                      value={formData.account_id || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, account_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger id="tx-account" className="bg-zinc-800 border-zinc-700" data-testid="tx-account-trigger">
                        <SelectValue placeholder={t('noLinkedAccount')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('noLinkedAccount')}</SelectItem>
                        {accounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.name}{accountTypeSuffix(acc.type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {accounts.length === 0 && (
                      <p className="text-xs text-zinc-500 mt-1">
                        <Link to="/accounts" className="text-violet-400 hover:underline">
                          + Crear una cuenta
                        </Link> para vincularla
                      </p>
                    )}
                  </div>
                  {formData.type === 'transfer' && (
                    <div>
                      <Label htmlFor="tx-to-account">{t('toAccount')}</Label>
                      <Select
                        value={formData.to_account_id || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, to_account_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger id="tx-to-account" className="bg-zinc-800 border-zinc-700" data-testid="tx-to-account-trigger">
                          <SelectValue placeholder={t('selectAccount')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('selectAccount')}</SelectItem>
                          {accounts.map(acc => (
                            <SelectItem key={acc.id} value={acc.id} disabled={acc.id === formData.account_id}>
                              {acc.name}{accountTypeSuffix(acc.type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {formData.type === 'expense' && (() => {
                    const selectedAcc = accounts.find(a => a.id === formData.account_id);
                    const isCC = selectedAcc?.type === 'credit_card';
                    return isCC ? (
                      <div>
                        <Label htmlFor="tx-installments">Cuotas</Label>
                        <Select
                          value={String(formData.installments_total)}
                          onValueChange={(v) => setFormData({ ...formData, installments_total: parseInt(v, 10) })}
                        >
                          <SelectTrigger id="tx-installments" className="bg-zinc-800 border-zinc-700" data-testid="tx-installments-trigger">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">A la vista (1x)</SelectItem>
                            {[2, 3, 4, 5, 6, 9, 10, 12, 18, 24].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}x cuotas</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.installments_total > 1 && formData.amount && (
                          <p className="text-xs text-zinc-500 mt-1">
                            Total: <span className="text-zinc-300">{fmt(parseFloat(formData.amount || 0) * formData.installments_total)}</span>
                            {' '}({formData.installments_total}× {fmt(parseFloat(formData.amount || 0))})
                          </p>
                        )}
                      </div>
                    ) : null;
                  })()}
                  <div>
                    <Label htmlFor="tx-notes">Notas (opcional)</Label>
                    <Input
                      id="tx-notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Detalles, recordatorio, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="tx-tags">Etiquetas (separadas por coma)</Label>
                    <Input
                      id="tx-tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="ej: viaje, trabajo, urgente"
                      data-testid="tx-tags-input"
                    />
                    {formData.tags && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {formData.tags.split(',').map(t => t.trim()).filter(Boolean).map((tag, i) => (
                          <span key={i} className="text-xs bg-violet-500/15 text-violet-300 border border-violet-500/30 px-2 py-0.5 rounded-full">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ReceiptUploader
                    value={formData.receipt_base64}
                    onChange={(v) => setFormData({ ...formData, receipt_base64: v })}
                    testId="tx-receipt"
                  />
                  <div className="flex flex-col">
                    <Label className="mb-2">Fecha</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="transaction-date-trigger"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white",
                            !formData.date && "text-zinc-400"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date ? format(formData.date, "PPP", { locale: es }) : <span>Selecciona una fecha</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700 text-white" align="start">
                        <CalendarPicker
                          mode="single"
                          selected={formData.date}
                          onSelect={(d) => d && setFormData({ ...formData, date: d })}
                          initialFocus
                          locale={es}
                          className="bg-zinc-900 text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-tx-btn">{t('save')}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* === EMPTY STATE === */}
        {hasNoData && (
          <Card className="bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30 mb-8" data-testid="empty-state">
            <CardContent className="p-6 sm:p-8 text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-violet-500/20 items-center justify-center mb-4">
                <Sparkles className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">¡Bienvenido a WSFinances!</h3>
              <p className="text-zinc-400 max-w-md mx-auto mb-6">
                Aún no tienes datos. Empieza con un par de acciones rápidas para
                tener tu radiografía financiera en segundos.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setDialogOpen(true)} data-testid="empty-add-tx">
                  <Plus className="h-4 w-4 mr-2" /> {t('addFirstTransaction')}
                </Button>
                <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200" onClick={() => setBudgetDialogOpen(true)} data-testid="empty-create-budget">
                  <Target className="h-4 w-4 mr-2" /> {t('createMonthlyBudget')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* === KEY STATS (answer the 4 dashboard questions) === */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Real balance today */}
          <Card className="stat-card border-zinc-800 card-hover" data-testid="card-real-balance">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-zinc-400 text-sm mb-1">{t('realBalanceToday')}</p>
                  <p className={`text-2xl sm:text-3xl font-bold truncate ${realBalance >= 0 ? 'text-white' : 'text-red-400'}`}>
                    {fmt(realBalance)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">acumulado total</p>
                </div>
                <div className="h-11 w-11 shrink-0 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-violet-400" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Income this month */}
          <Card className="stat-card border-zinc-800 card-hover" data-testid="card-month-income">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-zinc-400 text-sm mb-1">{t('incomeThisMonth')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-green-400 truncate">{fmt(monthIncome)}</p>
                  {incomeChange !== null && incomeChange !== undefined && (
                    <p className={`text-xs flex items-center gap-1 mt-1 ${incomeChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {incomeChange >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {Math.abs(incomeChange)}% vs mes anterior
                    </p>
                  )}
                </div>
                <div className="h-11 w-11 shrink-0 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-400" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses this month */}
          <Card className="stat-card border-zinc-800 card-hover" data-testid="card-month-expense">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-zinc-400 text-sm mb-1">{t('expensesThisMonth')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-red-400 truncate">{fmt(monthExpense)}</p>
                  {expenseChange !== null && expenseChange !== undefined && (
                    <p className={`text-xs flex items-center gap-1 mt-1 ${expenseChange > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {expenseChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {expenseChange > 0 ? '+' : ''}{expenseChange}% vs mes anterior
                    </p>
                  )}
                </div>
                <div className="h-11 w-11 shrink-0 rounded-full bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available to spend */}
          <Card className="stat-card border-zinc-800 card-hover" data-testid="card-available">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-zinc-400 text-sm mb-1">{t('availableToSpend')}</p>
                  <p className={`text-2xl sm:text-3xl font-bold truncate ${availableSpend >= 0 ? 'text-violet-300' : 'text-red-400'}`}>
                    {fmt(availableSpend)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {availableMode === 'budget' ? 'según tu presupuesto' : 'ingresos − gastos del mes'}
                  </p>
                </div>
                <div className="h-11 w-11 shrink-0 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Target className="h-5 w-5 text-blue-400" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* === UPCOMING BILLS + TOP CATEGORIES (side by side on lg) === */}
        {(upcomingBills.length > 0 || topCategories.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
            {/* Upcoming bills */}
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="card-upcoming-bills">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <CalendarClock className="h-5 w-5 text-amber-400" />
                  Próximos pagos (14 días)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingBills.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-2">
                    No tienes recurrentes próximos.{' '}
                    <Link to="/transactions" className="text-violet-400 hover:underline">
                      Configura uno
                    </Link>
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {upcomingBills.slice(0, 5).map(b => (
                      <li key={b.id} className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">{b.description}</p>
                          <p className="text-xs text-zinc-500">
                            {b.category} · {b.days_until === 0 ? 'hoy' : `en ${b.days_until} día${b.days_until === 1 ? '' : 's'}`}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-red-400">{fmt(b.amount)}</p>
                          <p className="text-xs text-zinc-500">{b.due_date}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Top categories */}
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="card-top-categories">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <PieChart className="h-5 w-5 text-violet-400" />
                  Top categorías del mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topCategories.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-2">Aún no hay gastos este mes.</p>
                ) : (
                  <ul className="space-y-3">
                    {topCategories.map((c, idx) => (
                      <li key={c.category}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-zinc-200 truncate">{idx + 1}. {c.category}</span>
                          <span className="text-zinc-300 font-medium shrink-0">{fmt(c.amount)}</span>
                        </div>
                        <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${c.percentage}%`,
                              backgroundColor: COLORS[idx % COLORS.length]
                            }}
                          />
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">{c.percentage}% de tus gastos</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* === BUDGETS === */}
        {budgets.length > 0 && (
          <div className="mb-8" data-testid="budgets-section">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-violet-400" />
              Presupuestos del Mes
              {budgetSummary && (
                <span className={`ml-auto text-sm font-normal ${budgetSummary.is_exceeded ? 'text-red-400' : 'text-zinc-400'}`}>
                  {fmt(budgetSummary.spent)} / {fmt(budgetSummary.total)} ({budgetSummary.percentage}%)
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {budgets.map((budget, idx) => (
                <Card key={idx} className={`border-zinc-800 ${budget.is_exceeded ? 'border-red-500/50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-white truncate">{budget.category}</span>
                      <span className={`text-sm font-bold shrink-0 ${budget.is_exceeded ? 'text-red-400' : budget.percentage >= 80 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {budget.percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all ${budget.is_exceeded ? 'bg-red-500' : budget.percentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(budget.percentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">{fmt(budget.spent)}</span>
                      <span className="text-zinc-500">/ {fmt(budget.amount)}</span>
                    </div>
                    {budget.is_exceeded && (
                      <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> ¡Presupuesto excedido!
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* === INSIGHTS === */}
        {insights.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-400" />
              Insights inteligentes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {insights.slice(0, 6).map((insight, idx) => (
                <div key={idx} className={`p-4 rounded-lg border ${getInsightBg(insight.type)}`}>
                  <div className="flex items-start gap-3">
                    {getInsightIcon(insight.type)}
                    <div className="min-w-0">
                      <p className="font-medium text-white">{insight.title}</p>
                      <p className="text-sm text-zinc-400 mt-1">{insight.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === CHARTS === */}
        {!hasNoData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Ingresos vs Gastos por mes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72 sm:h-80 min-h-[280px]">
                  {stats?.monthly_chart?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <BarChart data={stats.monthly_chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                        <YAxis stroke="#71717a" fontSize={12} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                          formatter={(v) => fmt(v)}
                        />
                        <Legend />
                        <Bar dataKey="income" name="Ingresos" fill="#4ade80" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="expense" name="Gastos" fill="#f87171" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
                      No hay datos para mostrar
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Gastos por categoría</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="min-h-[280px]">
                  {stats?.category_chart?.length > 0 ? (
                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(220px,280px)_1fr] gap-5 items-center">
                      <div className="h-64 sm:h-72 min-h-[240px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                          <RePieChart>
                            <Pie
                              data={stats.category_chart}
                              cx="50%"
                              cy="50%"
                              innerRadius={62}
                              outerRadius={104}
                              paddingAngle={2}
                              dataKey="value"
                              label={false}
                              labelLine={false}
                            >
                              {stats.category_chart.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                              formatter={(value) => [fmt(value), 'Monto']}
                            />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {stats.category_chart.map((entry, index) => {
                          const total = stats.category_chart.reduce((sum, item) => sum + Number(item.value || 0), 0);
                          const pct = total > 0 ? (Number(entry.value || 0) / total) * 100 : 0;
                          return (
                            <div key={entry.name || index} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-2">
                              <div className="flex items-center justify-between gap-3 text-sm">
                                <div className="min-w-0 flex items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                  />
                                  <span className="text-zinc-200 truncate">{entry.name}</span>
                                </div>
                                <span className="text-zinc-400 shrink-0">{pct.toFixed(0)}%</span>
                              </div>
                              <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${Math.max(2, pct)}%`,
                                    backgroundColor: COLORS[index % COLORS.length]
                                  }}
                                />
                              </div>
                              <p className="mt-1 text-xs text-zinc-500 text-right">{fmt(entry.value)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
                      No hay gastos registrados
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* === ACCOUNTS · GOALS · DEBTS QUICK SUMMARY === */}
        {(accountsSummary?.account_count > 0 || goalsSummary?.goal_count > 0 || debtsSummary?.debt_count > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="finance-overview">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-400 text-sm">{t('accounts')}</p>
                  <Wallet className="h-4 w-4 text-violet-400" />
                </div>
                {accountsSummary?.account_count > 0 ? (
                  <>
                    <p className="text-xl font-bold text-white">{fmt(accountsSummary.net_worth ?? accountsSummary.total_cash)}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Patrimonio · {accountsSummary.account_count} cuenta(s)
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Líquido {fmt(accountsSummary.total_liquid ?? accountsSummary.total_cash)}
                      {accountsSummary.total_investments > 0
                        ? ` · Inversiones ${fmt(accountsSummary.total_investments)}`
                        : ''}
                      {accountsSummary.total_credit_used > 0
                        ? ` · Tarjeta ${fmt(accountsSummary.total_credit_used)}`
                        : ''}
                    </p>
                  </>
                ) : (
                  <Link to="/accounts" className="text-sm text-violet-400 hover:underline">
                    + Añadir primera cuenta
                  </Link>
                )}
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-400 text-sm">{t('goalsAndDebts')}</p>
                  <PiggyBank className="h-4 w-4 text-emerald-400" />
                </div>
                {goalsSummary?.goal_count > 0 ? (
                  <>
                    <p className="text-xl font-bold text-emerald-400">{fmt(goalsSummary.total_saved)}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {goalsSummary.progress_percentage}% de {fmt(goalsSummary.total_target)}
                    </p>
                  </>
                ) : (
                  <Link to="/goals" className="text-sm text-violet-400 hover:underline">
                    + Crear primera meta
                  </Link>
                )}
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-400 text-sm">Deudas</p>
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                </div>
                {debtsSummary?.debt_count > 0 ? (
                  <>
                    <p className="text-xl font-bold text-red-400">{fmt(debtsSummary.total_balance)}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {debtsSummary.progress_percentage}% pagado · {debtsSummary.debt_count} deuda(s)
                    </p>
                  </>
                ) : (
                  <Link to="/goals" className="text-sm text-violet-400 hover:underline">
                    Sin deudas registradas
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* === RECENT TRANSACTIONS === */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-lg">{t('recentTransactions')}</CardTitle>
            <Link to="/transactions" className="text-violet-400 hover:text-violet-300 text-sm">
              Ver todas →
            </Link>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-zinc-500 mb-4">{t('noTransactionsRecorded')}</p>
                <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Añadir la primera
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((transaction) => {
                  const transactionType = normalizeTransactionType(transaction.type);
                  return (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center ${
                        transactionType === 'income'
                          ? 'bg-green-500/20'
                          : transactionType === 'transfer'
                            ? 'bg-sky-500/20'
                            : 'bg-red-500/20'
                      }`}>
                        {transactionType === 'income'
                          ? <TrendingUp className="h-5 w-5 text-green-400" />
                          : transactionType === 'transfer'
                            ? <ArrowLeftRight className="h-5 w-5 text-sky-300" />
                            : <TrendingDown className="h-5 w-5 text-red-400" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{transaction.category}</p>
                        <p className="text-xs sm:text-sm text-zinc-400 truncate">
                          {transaction.description || 'Sin descripción'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                      <div className="text-right">
                        <p className={`font-bold text-sm sm:text-base ${
                          transactionType === 'income'
                            ? 'text-green-400'
                            : transactionType === 'transfer'
                              ? 'text-sky-300'
                              : 'text-red-400'
                        }`}>
                          {transactionType === 'income' ? '+' : transactionType === 'expense' ? '-' : ''}{fmt(transaction.amount)}
                        </p>
                        <p className="text-xs text-zinc-500">{transaction.date}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-zinc-400 hover:text-red-400 h-8 w-8"
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        aria-label={`Eliminar transacción ${transaction.description || transaction.category}`}
                        data-testid={`delete-tx-${transaction.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )})}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardPage;
